import { useState, useRef, useEffect, useCallback } from 'react';
import { Search, X, ChevronDown, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { useDebouncedValue } from '@/lib/hooks/useDebouncedValue';

export interface LookupOption<T = any> {
  id: number | string;
  label: string;
  data: T;
  isGroup?: boolean; // Indicates this is a group header
  isDisabled?: boolean; // Indicates this option cannot be selected
  groupId?: string | number; // ID of the parent group (for child items)
}

export interface LookupProps<T = any> {
  value?: LookupOption<T> | null;
  onChange: (value: LookupOption<T> | null) => void;
  onSearch: (query: string) => void;
  onLoadMore?: () => void;
  onOpen?: () => void;
  options: LookupOption<T>[];
  isLoading?: boolean;
  hasMore?: boolean;
  placeholder?: string;
  emptyMessage?: string;
  className?: string;
  disabled?: boolean;
  renderOption?: (option: LookupOption<T>) => React.ReactNode;
}

export function Lookup<T = any>({
  value,
  onChange,
  onSearch,
  onLoadMore,
  onOpen,
  options,
  isLoading,
  hasMore,
  placeholder = 'Search...',
  emptyMessage = 'No results found',
  className,
  disabled,
  renderOption,
}: LookupProps<T>) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const optionRefs = useRef<(HTMLButtonElement | null)[]>([]);

  const debouncedSearch = useDebouncedValue(searchQuery, 300);

  // Trigger search when debounced value changes
  useEffect(() => {
    if (isOpen) {
      onSearch(debouncedSearch);
    }
  }, [debouncedSearch, isOpen, onSearch]);

  // Reset highlighted index when options change
  useEffect(() => {
    setHighlightedIndex(-1);
  }, [options]);

  // Scroll highlighted option into view
  useEffect(() => {
    if (highlightedIndex >= 0 && optionRefs.current[highlightedIndex]) {
      optionRefs.current[highlightedIndex]?.scrollIntoView({
        block: 'nearest',
        behavior: 'smooth',
      });
    }
  }, [highlightedIndex]);

  // Handle click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Handle scroll for infinite loading
  const handleScroll = useCallback(() => {
    if (!listRef.current || !hasMore || isLoading) return;

    const { scrollTop, scrollHeight, clientHeight } = listRef.current;
    if (scrollHeight - scrollTop <= clientHeight * 1.5) {
      onLoadMore?.();
    }
  }, [hasMore, isLoading, onLoadMore]);

  const handleSelect = (option: LookupOption<T>) => {
    onChange(option);
    setIsOpen(false);
    setSearchQuery('');
    setHighlightedIndex(-1);
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange(null);
    setSearchQuery('');
    setHighlightedIndex(-1);
  };

  const handleOpen = () => {
    if (!disabled) {
      setIsOpen(true);
      onOpen?.();
      if (!debouncedSearch) {
        onSearch('');
      }
    }
  };

  // Keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen) {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        handleOpen();
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex((prev) => {
          let next = prev + 1;
          // Skip groups and disabled items
          while (next < options.length && (options[next].isGroup || options[next].isDisabled)) {
            next++;
          }
          return next < options.length ? next : prev;
        });
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex((prev) => {
          let next = prev - 1;
          // Skip groups and disabled items
          while (next >= 0 && (options[next].isGroup || options[next].isDisabled)) {
            next--;
          }
          return next >= 0 ? next : -1;
        });
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && options[highlightedIndex]) {
          handleSelect(options[highlightedIndex]);
        }
        break;
      case 'Escape':
        e.preventDefault();
        setIsOpen(false);
        setHighlightedIndex(-1);
        break;
      case 'Home':
        e.preventDefault();
        setHighlightedIndex(0);
        break;
      case 'End':
        e.preventDefault();
        setHighlightedIndex(options.length - 1);
        break;
    }
  };

  return (
    <div ref={dropdownRef} className={cn('relative', className)}>
      <div
        onClick={handleOpen}
        onKeyDown={handleKeyDown}
        tabIndex={disabled ? -1 : 0}
        role="combobox"
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        aria-controls="lookup-listbox"
        className={cn(
          'flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-lg bg-white cursor-pointer',
          'hover:border-gray-400 focus-within:ring-2 focus-within:ring-primary-500 focus-within:border-primary-500',
          disabled && 'opacity-50 cursor-not-allowed bg-gray-50',
          className
        )}
      >
        <Search className="h-4 w-4 text-gray-400 flex-shrink-0" />
        <span
          className={cn('flex-1 min-w-0 truncate text-sm', !value && 'text-gray-500')}
          title={value?.label || undefined}
        >
          {value ? value.label : placeholder}
        </span>
        {value && !disabled && (
          <button
            onClick={handleClear}
            className="p-0.5 hover:bg-gray-100 rounded cursor-pointer"
            type="button"
            aria-label="Clear selection"
          >
            <X className="h-3.5 w-3.5 text-gray-500" />
          </button>
        )}
        <ChevronDown className="h-4 w-4 text-gray-400 flex-shrink-0" />
      </div>

      {isOpen && (
        <div className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg">
          <div className="p-2 border-b border-gray-200">
            <Input
              type="text"
              placeholder={placeholder}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={handleKeyDown}
              className="text-sm"
              autoFocus
              role="searchbox"
              aria-label="Search options"
            />
          </div>

          <div
            ref={listRef}
            onScroll={handleScroll}
            className="max-h-64 overflow-y-auto"
            role="listbox"
            id="lookup-listbox"
          >
            {isLoading && options.length === 0 ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
              </div>
            ) : options.length === 0 ? (
              <div className="py-8 text-center text-sm text-gray-500">{emptyMessage}</div>
            ) : (
              <>
                {options.map((option, index) => (
                  <button
                    key={option.id}
                    ref={(el) => {
                      optionRefs.current[index] = el;
                    }}
                    onClick={() => !option.isGroup && !option.isDisabled && handleSelect(option)}
                    onMouseEnter={() =>
                      !option.isGroup && !option.isDisabled && setHighlightedIndex(index)
                    }
                    className={cn(
                      'w-full px-3 py-2 text-left text-sm',
                      option.isGroup && 'font-semibold text-gray-700 bg-gray-50 cursor-default',
                      !option.isGroup && !option.isDisabled && 'hover:bg-gray-50 cursor-pointer',
                      option.isDisabled && 'opacity-50 cursor-not-allowed',
                      highlightedIndex === index &&
                        !option.isGroup &&
                        !option.isDisabled &&
                        'bg-gray-100',
                      option.groupId && !option.isGroup && 'pl-6'
                    )}
                    type="button"
                    role="option"
                    aria-selected={highlightedIndex === index}
                    disabled={option.isGroup || option.isDisabled}
                  >
                    {renderOption ? renderOption(option) : option.label}
                  </button>
                ))}
                {isLoading && (
                  <div className="flex items-center justify-center py-2">
                    <Loader2 className="h-4 w-4 animate-spin text-gray-400" />
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
